import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '../../../../components/input';
import { OTPInput } from '../../../../components/otp-input';
import editIcon from '../../../../../assets/icons/edit.png';
import { requestEmailChangeCode, validateEmailChangeCode } from '../../../../../insfrastructure/services/auth';
import { useOTPCountdown } from '../../../../hooks/useOTPCountdown';
import { useOTPValidation } from '../../../../hooks/useOTPValidation';

type EmailChangeState = 'idle' | 'requesting' | 'code_sent' | 'validating' | 'success' | 'error';

interface EmailChangeFieldProps {
  icon: any;
  label: string;
  currentEmail: string;
  onEmailChanged: (newEmail: string) => void;
  showLabel?: boolean;
}

const CODE_EXPIRY_SECONDS = 600; // 10 minutos
const RESEND_COOLDOWN_SECONDS = 60; // 60 segundos

export const EmailChangeField: React.FC<EmailChangeFieldProps> = ({
  icon,
  label,
  currentEmail,
  onEmailChanged,
  showLabel = true
}) => {
  const [state, setState] = useState<EmailChangeState>('idle');
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Hook para countdown de OTP
  const otpCountdown = useOTPCountdown({
    initialSeconds: CODE_EXPIRY_SECONDS,
    enabled: state === 'code_sent',
    onExpire: () => {
      setError('Código expirado. Solicite um novo código.');
    }
  });

  // Hook para validação de OTP
  const { validateCode } = useOTPValidation({ codeLength: 6 });

  // Limpar código quando estado muda
  useEffect(() => {
    if (state === 'idle' || state === 'success') {
      setCode('');
      setNewEmail('');
      setError('');
      otpCountdown.stop();
      setResendCooldown(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]); // otpCountdown.stop é estável e não precisa estar nas dependências

  // Cooldown para reenvio de código
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Validar formato de email
  const validateEmail = useCallback((email: string): string | null => {
    if (!email || email.trim() === '') {
      return 'O email não pode estar vazio';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Formato de email inválido';
    }
    
    if (email.trim().toLowerCase() === currentEmail.toLowerCase()) {
      return 'O novo email deve ser diferente do email atual';
    }
    
    return null;
  }, [currentEmail]);


  // Solicitar código OTP
  const handleRequestCode = useCallback(async () => {
    const emailError = validateEmail(newEmail);
    if (emailError) {
      setError(emailError);
      return;
    }

    setError('');
    setLoading(true);
    setState('requesting');

    try {
      await requestEmailChangeCode({ new_email: newEmail.trim().toLowerCase() });
      setState('code_sent');
      otpCountdown.start(CODE_EXPIRY_SECONDS);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error: any) {
      setState('error');
      let errorMessage = 'Erro ao solicitar código de verificação. Tente novamente.';
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        
        // Usar exatamente a mensagem que vem do backend
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'object') {
          // Tentar pegar o primeiro valor string do objeto
          const values = Object.values(errorData);
          const firstStringValue = values.find(v => typeof v === 'string');
          if (firstStringValue) {
            errorMessage = firstStringValue as string;
          } else if (values.length > 0) {
            const firstValue = values[0];
            if (Array.isArray(firstValue) && firstValue.length > 0) {
              errorMessage = firstValue[0];
            } else if (typeof firstValue === 'string') {
              errorMessage = firstValue;
            }
          }
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newEmail, validateEmail]); // otpCountdown.start é estável e não precisa estar nas dependências

  // Validar código e alterar email
  const handleValidateCode = useCallback(async (codeToValidate?: string) => {
    const codeToCheck = codeToValidate || code;
    const codeError = validateCode(codeToCheck);
    if (codeError) {
      setError(codeError);
      return;
    }

    setError('');
    setLoading(true);
    setState('validating');

    try {
      const codeToSend = codeToValidate || code;
      const response = await validateEmailChangeCode({
        new_email: newEmail.trim().toLowerCase(),
        code: codeToSend.trim()
      });
      
      setState('success');
      const updatedEmail = response.data?.new_email || newEmail.trim().toLowerCase();
      onEmailChanged(updatedEmail);
      
      // Voltar para idle após 3 segundos
      setTimeout(() => {
        setState('idle');
      }, 3000);
    } catch (error: any) {
      setState('code_sent');
      let errorMessage = 'Erro ao validar código. Verifique e tente novamente.';
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        
        // Usar exatamente a mensagem que vem do backend
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'object') {
          // Tentar pegar o primeiro valor string do objeto
          const values = Object.values(errorData);
          const firstStringValue = values.find(v => typeof v === 'string');
          if (firstStringValue) {
            errorMessage = firstStringValue as string;
          } else if (values.length > 0) {
            const firstValue = values[0];
            if (Array.isArray(firstValue) && firstValue.length > 0) {
              errorMessage = firstValue[0];
            } else if (typeof firstValue === 'string') {
              errorMessage = firstValue;
            }
          }
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setCode('');
    } finally {
      setLoading(false);
    }
  }, [code, newEmail, validateCode, onEmailChanged]);

  // Reenviar código
  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0) {
      return;
    }
    setError('');
    setCode('');
    await handleRequestCode();
  }, [handleRequestCode, resendCooldown]);

  // Cancelar processo
  const handleCancel = useCallback(() => {
    setState('idle');
    setNewEmail('');
    setCode('');
    setError('');
    otpCountdown.stop();
    setResendCooldown(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // otpCountdown.stop é estável e não precisa estar nas dependências

  // Formatar tempo do cooldown
  const formatCooldownTime = useCallback((seconds: number): string => {
    return `${seconds}s`;
  }, []);

  // Iniciar edição
  const handleStartEdit = useCallback(() => {
    setState('requesting');
    setNewEmail('');
    setError('');
  }, []);

  // Renderizar baseado no estado
  if (state === 'idle') {
    return (
      <div className='field-row'>
        {icon && <img src={icon} alt={label} className='field-icon' />}
        <div className='field-content'>
          {showLabel && <span className='field-label'>{label}:</span>}
          <span className='field-value'>{currentEmail || '-'}</span>
        </div>
        <img 
          src={editIcon} 
          alt='edit' 
          className='edit-icon' 
          onClick={handleStartEdit}
        />
      </div>
    );
  }

  if (state === 'requesting' || state === 'error') {
    return (
      <div className='field-row editing'>
        {icon && <img src={icon} alt={label} className='field-icon' />}
        <div className='edit-controls'>
          {showLabel && <span className='field-label'>{label}:</span>}
          <div className='email-change-form'>
            <div className='current-email-display'>
              <span>{currentEmail}</span>
            </div>
            <div className='new-email-input'>
              <Input
                name='new_email'
                type='email'
                value={newEmail}
                onChange={(val) => {
                  setNewEmail(val);
                  setError('');
                }}
                placeholder='Digite o novo email'
              />
            </div>
            {error && (
              <div className='error-message' style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                {error}
              </div>
            )}
            <div className='edit-actions'>
              <button 
                className='cancel-btn' 
                onClick={handleCancel}
                disabled={loading}
              >
                Cancelar
              </button>
              <button 
                className='save-btn' 
                onClick={handleRequestCode}
                disabled={loading || !newEmail.trim()}
              >
                {loading ? 'Enviando...' : 'Enviar Código'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'code_sent' || state === 'validating') {
    return (
      <div className='field-row editing'>
        {icon && <img src={icon} alt={label} className='field-icon' />}
        <div className='edit-controls'>
          {showLabel && <span className='field-label'>{label}:</span>}
          <div className='email-change-form'>
            <div className='current-email-display'>
              <span>{newEmail}</span>
              <span style={{ marginLeft: '5px', color: 'green' }}>✓</span>
            </div>
            <div style={{ marginTop: '10px', marginBottom: '5px', fontSize: '14px', color: '#666' }}>
              Código enviado para {newEmail}
            </div>
            <OTPInput
              value={code}
              onChange={(value) => {
                setCode(value);
                setError('');
              }}
              onComplete={(value) => {
                handleValidateCode(value);
              }}
              length={6}
              placeholder='000000'
              autoFocus
              disabled={loading}
            />
            {error && (
              <div className='error-message' style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                {error}
              </div>
            )}
            <div className='edit-actions' style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className='cancel-btn' 
                  onClick={handleCancel}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </button>
                <button 
                  className='save-btn' 
                  onClick={() => handleValidateCode()}
                  disabled={loading || code.length !== 6}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Validando...' : 'Validar Código'}
                </button>
              </div>
              <div style={{ textAlign: 'center' }}>
                <button 
                  onClick={handleResendCode}
                  disabled={loading || resendCooldown > 0}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: (loading || resendCooldown > 0) ? '#999' : '#0066cc',
                    textDecoration: 'underline',
                    cursor: (loading || resendCooldown > 0) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                >
                  {resendCooldown > 0 
                    ? `Reenviar código em ${formatCooldownTime(resendCooldown)}` 
                    : 'Reenviar Código'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className='field-row'>
        {icon && <img src={icon} alt={label} className='field-icon' />}
        <div className='field-content'>
          {showLabel && <span className='field-label'>{label}:</span>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span className='field-value'>{newEmail || currentEmail}</span>
            <span style={{ color: 'green', fontSize: '14px' }}>✅ E-mail alterado com sucesso!</span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
